// Small vanilla replacements for the jQuery helpers the theme used to rely on.

export const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

export const isVisible = (el) => !!el && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

export const offsetTop = (el) => el.getBoundingClientRect().top + window.scrollY;

export function show(el, display = 'block') {
    if (el) el.style.display = display;
}

export function hide(el) {
    if (el) el.style.display = 'none';
}

// Like jQuery's fx queue: a new fade/slide supersedes the running one, so a fadeIn
// requested mid-fadeOut wins instead of being hidden when the fadeOut settles.
const running = new WeakMap();

function animate(el, keyframes, duration, hiding, onDone) {
    running.get(el)?.animation.cancel();
    const animation = el.animate(keyframes, {duration, easing: 'ease'});
    running.set(el, {animation, hiding});
    return animation.finished
        .then(() => {
            running.delete(el);
            onDone();
        })
        .catch(() => {});
}

const isHiding = (el) => !!running.get(el)?.hiding;

// Visible and not on its way out: an element mid-fadeOut/slideUp still has layout
// but should count as hidden, so a show requested now isn't skipped.
export const isShown = (el) => isVisible(el) && !isHiding(el);

export function fadeIn(el, duration = 400, display = 'block') {
    if (!el) return Promise.resolve();
    el.style.display = display;
    return animate(el, [{opacity: 0}, {opacity: 1}], duration, false, () => { el.style.opacity = ''; });
}

export function fadeOut(el, duration = 400) {
    if (!el || isHiding(el)) return Promise.resolve();
    if (!isVisible(el)) {
        el.style.display = 'none';
        return Promise.resolve();
    }
    return animate(el, [{opacity: 1}, {opacity: 0}], duration, true, () => { el.style.display = 'none'; });
}

export function fadeTo(el, duration, opacity) {
    if (!el) return Promise.resolve();
    const from = getComputedStyle(el).opacity;
    return animate(el, [{opacity: from}, {opacity}], duration, false, () => { el.style.opacity = String(opacity); });
}

export function slideDown(el, duration = 400, display = 'block') {
    if (!el || isShown(el)) return Promise.resolve();
    el.style.display = display;
    el.style.overflow = 'hidden';
    const from = isHiding(el) ? el.offsetHeight : 0;
    const height = el.scrollHeight;
    return animate(el, [{height: `${from}px`}, {height: `${height}px`}], duration, false, () => { el.style.overflow = ''; });
}

export function slideUp(el, duration = 400) {
    if (!el || !isShown(el)) return Promise.resolve();
    el.style.overflow = 'hidden';
    return animate(el, [{height: `${el.offsetHeight}px`}, {height: '0px'}], duration, true, () => {
        el.style.display = 'none';
        el.style.overflow = '';
    });
}

export function scrollToY(top) {
    window.scrollTo({top, behavior: 'smooth'});
}

export function getCookie(name) {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${encodeURIComponent(name)}=`));
    if (!match) return undefined;
    const value = match.split('=').slice(1).join('=');
    try {
        return decodeURIComponent(value);
    } catch (e) {
        return value;
    }
}

export function setCookie(name, value, days) {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(String(value))}; path=/`;
    if (days) {
        const expires = new Date(Date.now() + days * 864e5);
        cookie += `; expires=${expires.toUTCString()}`;
    }
    document.cookie = cookie;
}
